<template>
  <v-container fluid>
    <v-card class="ma-5" elevation="8">
      <v-card-title>
        Uploaded Files
        <v-spacer/>

        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on" @click="initRefreshData" :loading="isRefreshingData">
              <v-icon>mdi-reload</v-icon>
            </v-btn>
          </template>
          <span>Reload</span>
        </v-tooltip>
      </v-card-title>

      <v-card-text> <!-- https://codepen.io/lzhoucs/pen/aadaJx -->
        <v-data-table :items="filteredFiles" :headers="headers" :search="search" height="calc(100vh - 290px)" fixed-header hide-default-footer disable-pagination>
          <!-- Empty table -->
          <template v-slot:no-data>
            <v-alert class="ma-3" color="primary" dark icon="mdi-alert-circle">There are no uploaded files</v-alert>
          </template>

          <!-- Search -->
          <template v-slot:top>
            <v-row no-gutters>
              <v-col sm="3">
                <v-text-field v-model="search" label="Search" class="mx-4" prepend-icon="mdi-magnify" clearable/>
              </v-col>

              <v-col sm="2">
                <v-select v-model="regionSelected" :items="regions" item-text="REGION" return-object label="Region" class="mx-4" prepend-icon="mdi-earth" clearable/>
              </v-col>

              <v-col sm="2">
                <v-select v-model="fileTypeSelected" :items="fileTypes" item-text="TIPO_FICHERO" return-object label="File type" class="mx-4" prepend-icon="mdi-file" clearable/>
              </v-col>
            </v-row>
          </template>

          <!-- Date row -->
          <template v-slot:[`item.FECHA`]="{ item }">
            <span>{{ item.FECHA | formatDate }}</span>
          </template>

          <!-- FileSize row -->
          <template v-slot:[`item.SIZE_KB`]="{ item }">
            <span>{{ item.SIZE_KB | fileSize }}</span>
          </template>

          <!-- Actions row -->
          <template v-slot:[`item.CONTROLS`]="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" @click="openFileMap(item)" :disabled="!item.TIPO_FICHERO.VISIBLE_MAPA">
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
              </template>
              <span>View on map</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" @click="downloadFile(item)">
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </template>
              <span>Download</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" @click="initDeleteFile(item)" :disabled="!item.TIPO_FICHERO.BORRABLE">
                  <v-icon color="red">mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <vx-upload-dialog v-model="showUploadDialog"/>

    <v-btn color="primary" fab x-large dark fixed bottom right class="v-btn--example" @click="openUploadDialog">
      <v-icon>mdi-file-plus</v-icon>
    </v-btn>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import UploadDialog from './UploadDialog.vue'

export default {
  name: 'FileManager',

  components: {
    'vx-upload-dialog': UploadDialog
  },

  filters: {
    fileSize: function (value) {
      const thresh = 1024
      const dp = 2

      if (Math.abs(value) < thresh) {
        return value + ' kB'
      }

      const units = ['MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
      let u = -1
      const r = 10 ** dp

      do {
        value /= thresh
        ++u
      } while (Math.round(Math.abs(value) * r) / r >= thresh && u < units.length - 1)

      return value.toFixed(dp) + ' ' + units[u]
    }
  },

  data: () => ({
    headers: [
      { text: 'Filename', value: 'NOMBRE' },
      { text: 'Type', value: 'TIPO_FICHERO.TIPO_FICHERO' },
      { text: 'Region', value: 'REGION.REGION' },
      { text: 'Description', value: 'DESCRIPCION' },
      { text: 'Upload date', value: 'FECHA' },
      { text: 'File size', value: 'SIZE_KB' },
      { text: 'Actions', value: 'CONTROLS', align: 'center', sortable: false }
    ],

    search: null,
    regionSelected: null,
    fileTypeSelected: null,

    isRefreshingData: false,
    showUploadDialog: false
  }),

  computed: {
    ...mapGetters('file', [
      'files',
      'fileTypes'
    ]),
    ...mapGetters('region', [
      'regions'
    ]),
    filteredFiles () {
      let files = this.files

      if (this.regionSelected) {
        files = this.files.filter(x => x.ID_REGION === this.regionSelected.ID_REGION)
      }

      if (this.fileTypeSelected) {
        files = files.filter(x => x.ID_TIPO_FICHERO === this.fileTypeSelected.ID_TIPO_FICHERO)
      }

      return files
    }
  },

  methods: {
    ...mapActions('file', [
      'getFiles',
      'downloadFile',
      'deleteFile'
    ]),
    initRefreshData () {
      this.isRefreshingData = true

      this.getFiles().catch(() => {
        this.$toastr.e('Error updating data')
      }).finally(() => {
        this.isRefreshingData = false
      })
    },
    async initDeleteFile (file) {
      this.$root.$confirmDialog.open('Delete file', 'Are you sure you want to delete the file?').then(async result => {
        if (result) {
          try {
            await this.deleteFile(file)

            this.$toastr.s('File deleted correctly')
          } catch (err) {
            this.$toastr.e('Error deleting file')
          }
        }
      })
    },
    openFileMap (file) {
      this.$router.push({ path: 'map', query: { idFile: file.ID_FICHERO } })
    },
    openUploadDialog () {
      this.showUploadDialog = true
    }
  },

  mounted () {
  }
}
</script>

<style scoped>
</style>
